/**
 * Config entity-type presentation — a plain-words label per stored `entityType`
 * (DNKADM-0392), used by both the entity-type filter control and the per-entry
 * entity chip.
 *
 * Keyed by the strings dink-api actually writes (`CONFIG_ENTITY_TYPES` — the
 * writers' `ENTITY_TYPE` constants, e.g. `v2_service`). An unrecognized type still
 * renders: the parser carries it verbatim (see `api/configAudit.ts`) and this module
 * turns it into readable words (`v2_court_surface` → "Court surface") rather than
 * showing the raw code alone, so a new config table lands without a code change and
 * the screen never throws on upstream drift.
 */
import type { ConfigEntityType } from '@/api/configAudit';

/** Plain-words label for each stored config entity type. */
const ENTITY_LABELS: Record<ConfigEntityType, string> = {
  v2_location: 'Location',
  v2_location_hours: 'Location hours',
  v2_location_hours_exception: 'Location hours exception',
  v2_service: 'Service',
  v2_service_category: 'Service category',
  v2_service_variation: 'Service variation',
  v2_service_category_assignment: 'Service category assignment',
  v2_service_location_assignment: 'Service location assignment',
  v2_service_staff_assignment: 'Service staff assignment',
  v2_service_resource_assignment: 'Service resource assignment',
  v2_variation_weekly_price: 'Variation weekly price',
  v2_variation_date_price: 'Variation date price',
  v2_staff_variation_price: 'Staff price exception',
  v2_resource: 'Resource',
  v2_resource_location_assignment: 'Resource location assignment',
  v2_resource_schedule: 'Resource schedule',
  v2_resource_date_exception: 'Resource date exception',
  v2_staff: 'Staff',
  v2_staff_location_assignment: 'Staff location assignment',
  v2_staff_schedule: 'Staff schedule',
  v2_staff_date_exception: 'Staff date exception',
  v2_staff_appointment_cap: 'Staff appointment cap',
  v2_staff_google_credential: 'Staff calendar connection',
  v2_membership_plan: 'Membership plan',
  v2_membership_plan_credit: 'Membership plan credit',
  v2_membership_plan_access_hours: 'Membership plan access hours',
  v2_membership_settings: 'Membership settings',
  v2_cancellation_band: 'Cancellation band',
  v2_daily_cap: 'Daily cap',
  v2_offering: 'Offering',
  v2_offering_credit: 'Offering credit',
  v2_credit_bucket: 'Credit bucket',
  v2_member_profile_field: 'Profile field',
  v2_tenant_payment_method: 'Payment method',
  v2_tax_rate: 'Tax rate',
  v2_tenant_settings: 'Tenant settings',
  v2_admin_caches: 'Admin caches',
};

/**
 * Readable words for a type with no label: drop the `v2_` prefix, split on
 * underscores, capitalise the first word (`v2_court_surface` → "Court surface").
 * A string that leaves nothing after the prefix falls back to the raw value.
 */
function humanizeEntityType(type: string): string {
  const words = type
    .replace(/^v2_/, '')
    .split(/[_\s]+/)
    .filter((word) => word.length > 0);
  if (words.length === 0) return type;
  const text = words.join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Label for an entity type. A known type maps to its plain-words label; an
 * unrecognized value is humanised (never the raw code alone); `null` shows a
 * placeholder.
 */
export function entityTypeLabel(type: ConfigEntityType | string | null): string {
  if (type === null) return '—';
  return (ENTITY_LABELS as Record<string, string>)[type] ?? humanizeEntityType(type);
}
